import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play, Sparkles, X } from 'lucide-react';
import { Link } from 'wouter';
import { useEffect, useState } from 'react';
import demoVideo from '@/assets/BloomDemoVid2.mp4';

const Hero = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  useEffect(() => {
    // Add gradient animation styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes gradientAnimation {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Close video on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVideoOpen(false);
    };
    if (isVideoOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isVideoOpen]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section className="relative pt-24 pb-16 overflow-hidden bg-white dark:bg-gray-900">
      {/* Vibrant animated diagonal gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(120deg,
            #06b6d4 0%,
            #14b8a6 25%,
            #10b981 50%,
            #22c55e 75%,
            #3b82f6 100%)`,
          backgroundSize: '400% 400%',
          animation: 'gradientAnimation 20s ease infinite'
        }}
      />
      
      {/* Light overlay for better text readability */}
      <div className="absolute inset-0 bg-white/20 dark:bg-gray-900/20" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Enhanced Badge */}
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center px-6 py-3 bg-white/15 dark:bg-gray-800/15 backdrop-blur-sm border border-white/40 dark:border-gray-700/40 rounded-full text-cyan-700 dark:text-cyan-300 text-sm font-medium mb-8 shadow-lg group"
            whileHover={{ scale: 1.05 }}
          >
            <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
            <span className="w-2 h-2 bg-cyan-500 rounded-full mr-3 animate-pulse"></span>
            Made for Soccer Coaches
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            variants={itemVariants}
            className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
          >
            <span className="text-gray-900 dark:text-white">Revolutionize</span>{' '}
            <span className="bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent">
              Your Soccer
            </span>
            <br />
            <span className="text-gray-900 dark:text-white">Recruitment</span>{' '}
            <span className="bg-gradient-to-r from-cyan-600 via-teal-600 to-blue-600 bg-clip-text text-transparent">
              with Bloom
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={itemVariants}
            className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            Discover exceptional talent faster with our AI-powered email extension designed specifically for soccer coaches. 
            Save hours of administrative work and focus on building your winning team.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
          >
            <Link href="/register">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  size="lg" 
                  className="relative bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 hover:from-cyan-600 hover:via-teal-600 hover:to-emerald-600 text-white px-10 py-5 text-lg font-semibold shadow-2xl transition-all duration-500 group overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative z-10">Test it Out</span>
                  <ArrowRight className="ml-3 w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </motion.div>
            </Link>
            
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="outline" 
                size="lg" 
                className="relative border-2 border-gray-300 dark:border-gray-600 hover:border-cyan-500 dark:hover:border-cyan-400 text-gray-700 dark:text-gray-300 hover:text-white px-10 py-5 text-lg font-semibold transition-all duration-500 hover:bg-gradient-to-r hover:from-cyan-500 hover:to-teal-500 group overflow-hidden bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm"
                onClick={() => setIsVideoOpen(true)}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Play className="mr-3 w-5 h-5 relative z-10" />
                <span className="relative z-10">Watch Demo</span>
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Video Modal */}
      {isVideoOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setIsVideoOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-5xl bg-black rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-colors duration-200"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Video player */}
            <video
              className="w-full h-auto"
              controls
              autoPlay
              controlsList="nodownload"
            >
              <source src={demoVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default Hero; 